import React from 'react';
import { Box, Typography, FormControl, Select, MenuItem } from '@mui/material';
import { SalesforceTargetObjectName } from '../../../types/salesforce';
import { HubSpotTargetObjectName } from '../../../types/hubspot';
import { CampaignSpreadsheetViewType } from '../../../context/CampaignContext';

export type CrmProvider = 'salesforce' | 'hubspot';

/** Campaign type from campaign settings. Used to show only relevant CRM target objects. */
export type CampaignType = 'account' | 'opportunity';

interface TargetObjectSelectorProps {
  crm: CrmProvider;
  /** When set, only target objects relevant to this campaign type are shown. Lead (SF) is never shown (no lead-search sync support). */
  campaignType?: CampaignType | null;
  viewType?: CampaignSpreadsheetViewType;
  targetObject: string | null;
  isEditMode: boolean;
  onTargetObjectChange: (value: string) => void;
}

const SALESFORCE_OPTIONS: { value: string; label: string }[] = [
  { value: SalesforceTargetObjectName.ACCOUNT, label: 'Account' },
  { value: SalesforceTargetObjectName.CONTACT, label: 'Contact' },
  { value: SalesforceTargetObjectName.LEAD, label: 'Lead' },
  { value: SalesforceTargetObjectName.OPPORTUNITY, label: 'Opportunity' }
];

const HUBSPOT_OPTIONS: { value: string; label: string }[] = [
  { value: HubSpotTargetObjectName.COMPANY, label: 'Company' },
  { value: HubSpotTargetObjectName.CONTACT, label: 'Contact' }
];

/** Lead (Salesforce) is not shown — we do not support syncing lead search to the CRM. */
/** Account campaigns: lead view → Contact; else → Company/Account. */
function getCrmObjectOptions(
  crm: CrmProvider,
  campaignType?: CampaignType | null,
  viewType?: CampaignSpreadsheetViewType
): { value: string; label: string }[] {
  if (campaignType === 'account') {
    const useContact = viewType === 'lead';
    if (crm === 'hubspot') {
      return HUBSPOT_OPTIONS.filter(
        (opt) =>
          opt.value ===
          (useContact
            ? HubSpotTargetObjectName.CONTACT
            : HubSpotTargetObjectName.COMPANY)
      );
    }
    return SALESFORCE_OPTIONS.filter(
      (opt) =>
        opt.value ===
        (useContact
          ? SalesforceTargetObjectName.CONTACT
          : SalesforceTargetObjectName.ACCOUNT)
    );
  }
  if (crm === 'hubspot') return HUBSPOT_OPTIONS;
  return SALESFORCE_OPTIONS.filter(
    (opt) => opt.value !== SalesforceTargetObjectName.LEAD
  );
}

/** Returns the set of target object values valid for the given CRM and campaign type (for validation in parent). */
export function getValidTargetValuesForCrm(
  crm: CrmProvider,
  campaignType?: CampaignType | null,
  viewType?: CampaignSpreadsheetViewType
): Set<string> {
  const options = getCrmObjectOptions(crm, campaignType, viewType);
  return new Set(options.map((o) => o.value));
}

const TargetObjectSelector: React.FC<TargetObjectSelectorProps> = ({
  crm,
  campaignType,
  viewType,
  targetObject,
  isEditMode,
  onTargetObjectChange
}) => {
  const options = getCrmObjectOptions(crm, campaignType, viewType);

  if (!isEditMode && targetObject) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Target Object
        </Typography>
        <Typography variant="body1">
          {options.find((opt) => opt.value === targetObject)?.label ||
            targetObject}
        </Typography>
      </Box>
    );
  }

  if (isEditMode) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Target Object
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={targetObject || ''}
            onChange={(e) => onTargetObjectChange(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select object...</em>
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return null;
};

export default TargetObjectSelector;
